// src/pages/Settings.tsx
import { useEffect, useState, useRef, createContext, useContext } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "@/lib/firebase";
import { getConfigDoc, setConfigDoc, getSyncLogs } from "@/lib/firestore";
import {
  getAllIntegrations, getIntegration, saveIntegrationConfig, setIntegrationEnabled,
  INTEGRATION_DEFAULTS,
  DEFAULT_MAX_INFO_ROUNDS, MIN_MAX_INFO_ROUNDS, MAX_MAX_INFO_ROUNDS,
} from "@/lib/integrations";
import {
  AiIntegrationConfig, AiProviderId, BrandingConfig, BusinessSchedule, EmailIntegrationConfig, EmailProviderId,
  GwsIntegrationConfig, HelpDeskUser, IntegrationDoc, OpenRouterModel, SecretMetadata,
  SlackIntegrationConfig, SlaConfig, SyncLog, UserRole,
} from "@/types";
import { SECRET_GUIDES } from "@/lib/secretGuides";
import { useAuth } from "@/lib/auth";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Toggle, Spinner, toast } from "@/components/shared";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

// ─── Admin gating ─────────────────────────────────────────────────
//
// /settings is reachable by agents and admins (RequireAgent in main.tsx),
// but almost every panel writes an admin-only resource: Firestore rules
// gate config/* and auditLog to admins, and integrations/* is now
// callable-only (saveIntegrationSettings, which asserts the admin claim).
// Agents therefore get a read-only view: commit controls are hidden and
// the panel body is wrapped in a disabled <fieldset> so every remaining
// input is inert — belt and braces, since the server is the real gate.

const AdminContext = createContext(false);
const useIsAdmin = () => useContext(AdminContext);

function ReadOnlyNotice() {
  return (
    <div role="status" className="card"
      style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "10px 12px", marginBottom: 14, background: "var(--orange-l)", border: "none" }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--orange-t)" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
        <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" />
      </svg>
      <div style={{ fontSize: 12, color: "var(--orange-t)" }}>
        <strong>Read-only.</strong> These settings require the admin role. You can review the
        current configuration, but changing it is restricted to administrators.
      </div>
    </div>
  );
}

/** Wraps an admin-only panel: shows the read-only notice to non-admins and
 *  makes the whole subtree inert. `fieldset[disabled]` natively disables
 *  every descendant input, select, textarea, and button, so a write
 *  control added later is inert by default rather than by remembering. */
function AdminGate({ children }: { children: React.ReactNode }) {
  const isAdmin = useIsAdmin();
  if (isAdmin) return <>{children}</>;
  return (
    <>
      <ReadOnlyNotice />
      {/* minInlineSize: fieldset defaults to min-content, which breaks the
          flex layouts inside the panels. */}
      <fieldset disabled style={{ border: "none", padding: 0, margin: 0, minInlineSize: "auto" }}>
        {children}
      </fieldset>
    </>
  );
}

const DEFAULT_BUSINESS_SCHEDULE: BusinessSchedule = {
  enabled: false,
  timezone: typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
    : "UTC",
  workingDays: [1, 2, 3, 4, 5],
  workingHours: { start: "09:00", end: "17:00" },
  holidays: [],
};

const DEFAULT_SLA: SlaConfig = {
  P1: { responseHours: 1, resolutionHours: 4 },
  P2: { responseHours: 2, resolutionHours: 8 },
  P3: { responseHours: 4, resolutionHours: 24 },
  P4: { responseHours: 8, resolutionHours: 72 },
  businessSchedule: DEFAULT_BUSINESS_SCHEDULE,
};

// A small curated list of common timezones, plus the user's local one.
// IANA has thousands; this covers ~99% of helpdesk deployments without
// turning the picker into a research project.
const COMMON_TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "America/Halifax", "America/Toronto",
  "America/Mexico_City", "America/Sao_Paulo",
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin",
  "Europe/Madrid", "Europe/Stockholm", "Europe/Athens", "Europe/Moscow",
  "Africa/Cairo", "Africa/Johannesburg",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Shanghai",
  "Asia/Singapore", "Asia/Tokyo", "Asia/Seoul",
  "Australia/Perth", "Australia/Sydney", "Pacific/Auckland",
  "UTC",
];

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const PANELS = [
  { id: "branding",     label: "Branding",            icon: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" },
  { id: "ai",           label: "AI & Triage",          icon: "M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z" },
  { id: "sla",          label: "SLA Rules",            icon: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" },
  { id: "users",        label: "Users",                icon: "M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" },
  { id: "csv",          label: "CSV Import",           icon: "M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" },
  { id: "integrations", label: "All Integrations",     icon: "M19 7h-3V6c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v1H5c-1.1 0-2 .9-2 2v11c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2z" },
  { id: "gws",          label: "Google Workspace",     icon: "M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" },
  { id: "slack",        label: "Slack",                icon: "M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" },
  { id: "email",        label: "Email",                icon: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" },
  { id: "secrets",      label: "API Keys",             icon: "M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" },
];

function SCard({ title, subtitle, icon, iconBg, iconColor, badge, children }: {
  title: string; subtitle: string; icon: string; iconBg: string; iconColor: string;
  badge?: { label: string; type: "success" | "warn" | "gem" }; children: React.ReactNode;
}) {
  const badgeBg = badge?.type === "success" ? "var(--green-l)" : badge?.type === "gem" ? "var(--gem-l)" : "var(--orange-l)";
  const badgeColor = badge?.type === "success" ? "var(--green-t)" : badge?.type === "gem" ? "var(--gem-t)" : "var(--orange-t)";
  return (
    <div className="card" style={{ marginBottom: 14, overflow: "hidden" }}>
      <div className="card-hdr" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill={iconColor}><path d={icon} /></svg>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 1 }}>{subtitle}</div>
        </div>
        {badge && (
          <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20, background: badgeBg, color: badgeColor }}>
            {badge.label}
          </span>
        )}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function ToggleRow({ title, subtitle, value, onChange }: { title: string; subtitle?: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: "0.5px solid var(--border)" }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 1 }}>{subtitle}</div>}
      </div>
      <Toggle value={value} onChange={onChange} />
    </div>
  );
}

// ─── Branding Panel ───────────────────────────────────────────────

function BrandingPanel() {
  const isAdmin = useIsAdmin();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#0071e3");
  const [portalName, setPortalName] = useState("HelpDesk");
  const [customDomain, setCustomDomain] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getConfigDoc<BrandingConfig>("branding").then((cfg) => {
      if (!cfg) return;
      if (cfg.primaryColor) setPrimaryColor(cfg.primaryColor);
      if (cfg.portalName) setPortalName(cfg.portalName);
      if (cfg.logoUrl) setLogoPreview(cfg.logoUrl);
      if (cfg.customDomain) setCustomDomain(cfg.customDomain);
    });
  }, []);

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) { toast("Please upload an image file", "error"); return; }
    if (file.size > 2 * 1024 * 1024) { toast("File must be under 2MB", "error"); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const saveLogo = async () => {
    if (!logoFile) return;
    setSaving(true);
    try {
      const storageRef = ref(storage, `branding/logo`);
      await uploadBytes(storageRef, logoFile);
      const logoUrl = await getDownloadURL(storageRef);
      await setConfigDoc<Partial<BrandingConfig>>("branding", { logoUrl });
      setLogoPreview(logoUrl);
      setLogoFile(null);
      toast("Logo saved");
    } catch { toast("Failed to save logo", "error"); }
    setSaving(false);
  };

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      await setConfigDoc<Partial<BrandingConfig>>("branding", {
        primaryColor,
        portalName,
        customDomain: customDomain.trim(),
      });
      toast("Branding saved");
    } catch { toast("Failed to save branding", "error"); }
    setSavingBrand(false);
  };

  return (
    <>
      <SCard title="Company Logo" subtitle="Shown in the header and user-facing portal"
        icon="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        iconBg="var(--orange-l)" iconColor="var(--orange)"
        badge={logoPreview ? { label: "Ready to save", type: "gem" } : { label: "Not set", type: "warn" }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
              style={{
                border: `2px dashed ${logoPreview ? "var(--green)" : "var(--border-med)"}`,
                borderRadius: "var(--r)", padding: 24, textAlign: "center", cursor: "pointer",
                background: logoPreview ? "var(--green-l)" : "var(--gray)", transition: "all .2s",
              }}
            >
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
              {logoPreview ? (
                <>
                  <img src={logoPreview} alt="Logo preview" style={{ maxHeight: 60, maxWidth: 180, objectFit: "contain", borderRadius: 4 }} />
                  <div style={{ fontSize: 12, color: "var(--green-t)", marginTop: 6, fontWeight: 500 }}>✓ {logoFile?.name}</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 36, marginBottom: 8 }}>🖼</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Drop your logo here</div>
                  <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 3 }}>or click to browse · PNG, SVG, JPG · Max 2MB</div>
                  <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 6 }}>Recommended: 200×60px, transparent background</div>
                </>
              )}
            </div>
            {logoPreview && isAdmin && (
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="btn btn-green btn-sm" onClick={saveLogo} disabled={saving}>
                  {saving ? <Spinner size={12} color="var(--green-t)" /> : "✓ Save Logo"}
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => { setLogoFile(null); setLogoPreview(null); }}>Remove</button>
              </div>
            )}
          </div>
          <div style={{ width: 120, flexShrink: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 8, textTransform: "uppercase", letterSpacing: .4 }}>Preview</div>
            <div style={{ background: "white", border: "0.5px solid var(--border)", borderRadius: "var(--rs)", padding: 12, display: "flex", alignItems: "center", justifyContent: "center", height: 58, marginBottom: 8 }}>
              {/* This tile is deliberately white in both themes (it previews the
                  logo on a light header), so the placeholder colour is pinned —
                  var(--text3) is near-white in dark mode and vanishes here. */}
              {logoPreview ? <img src={logoPreview} style={{ maxHeight: 34, maxWidth: 90, objectFit: "contain" }} alt="" /> : <span style={{ fontSize: 12, color: "#86868b" }}>No logo</span>}
            </div>
            <div style={{ background: "#1d1d1f", borderRadius: "var(--rs)", padding: 10, display: "flex", alignItems: "center", justifyContent: "center", height: 48 }}>
              {logoPreview ? <img src={logoPreview} style={{ maxHeight: 28, maxWidth: 90, objectFit: "contain" }} alt="" /> : <span style={{ fontSize: 11, color: "rgba(255,255,255,.3)" }}>Dark bg</span>}
            </div>
          </div>
        </div>
      </SCard>

      <SCard title="Brand Colors" subtitle="Primary accent color used in buttons and highlights"
        icon="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"
        iconBg="var(--blue-l)" iconColor="var(--blue)"
      >
        <div className="frow" style={{ marginBottom: 14 }}>
          <div className="fg">
            <label>Primary Color</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} style={{ width: 40, height: 36, border: "1px solid var(--border-med)", borderRadius: 6, cursor: "pointer", background: "none" }} />
              <input className="fi" style={{ flex: 1 }} value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} placeholder="#0071e3" />
            </div>
          </div>
          <div className="fg">
            <label>Portal Name</label>
            <input className="fi" value={portalName} onChange={(e) => setPortalName(e.target.value)} />
          </div>
        </div>
        <div className="fg" style={{ marginBottom: 14 }}>
          <label>Custom Domain <span style={{ color: "var(--text2)", fontWeight: 400 }}>— used in outgoing-email ticket links</span></label>
          <input className="fi" value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value)}
            placeholder="ticketwhip.com" />
          <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 4, lineHeight: 1.5 }}>
            Leave blank to use the default Firebase Hosting URL. The domain must be added to Firebase Hosting (Console → Hosting → Add custom domain) and pointed at this site for the link to actually resolve. <code>https://</code> is added automatically.
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="btn btn-primary btn-sm" onClick={saveBrand} disabled={savingBrand}>
              {savingBrand ? <Spinner size={12} color="white" /> : "Save Branding"}
            </button>
          </div>
        )}
      </SCard>
    </>
  );
}

// ─── AI & Triage Panel ────────────────────────────────────────────

const PROVIDER_MODELS: Record<AiProviderId, { value: string; label: string }[]> = {
  gemini: [
    { value: "gemini-2.5-flash",    label: "gemini-2.5-flash (recommended)" },
    { value: "gemini-2.5-pro",      label: "gemini-2.5-pro" },
    { value: "gemini-flash-latest", label: "gemini-flash-latest (auto-update)" },
  ],
  openai: [
    { value: "gpt-4o-mini",         label: "gpt-4o-mini (recommended)" },
    { value: "gpt-4o",              label: "gpt-4o" },
  ],
  anthropic: [
    { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (recommended)" },
    { value: "claude-sonnet-4-6",   label: "Claude Sonnet 4.6" },
  ],
  // OpenRouter's list is fetched live from its catalog — see
  // OpenRouterModelPicker below. This stays empty so the static-dropdown
  // path is never taken for it.
  openrouter: [],
};

// Keep in sync with DEPRECATED_GEMINI_MODELS in functions/src/lib/aiProvider.ts
const DEPRECATED_MODELS = new Set(["gemini-2.0-flash", "gemini-1.5-flash", "gemini-1.5-pro", "gemini-pro"]);

// ─── OpenRouter model picker ──────────────────────────────────────

/** $/1M tokens. Sub-cent prices need more precision than a plain
 *  toFixed(2), which would render most of the cheap catalog as "$0.00". */
function formatPer1M(value: number): string {
  if (value === 0) return "free";
  if (value < 0.01) return `$${value.toFixed(4)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

function formatContext(tokens: number): string {
  if (!tokens) return "—";
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(tokens % 1_000_000 === 0 ? 0 : 1)}M ctx`;
  if (tokens >= 1000) return `${Math.round(tokens / 1000)}K ctx`;
  return `${tokens} ctx`;
}

function OpenRouterModelPicker({ selected, onSelect }: {
  selected: string;
  onSelect: (model: OpenRouterModel) => void;
}) {
  const [models, setModels] = useState<OpenRouterModel[] | null>(null);
  const [excluded, setExcluded] = useState(0);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [freeOnly, setFreeOnly] = useState(false);

  useEffect(() => {
    const fn = httpsCallable<unknown, { models: OpenRouterModel[]; excluded?: number }>(getFunctions(), "listOpenRouterModels");
    fn({})
      .then((res) => { setModels(res.data.models); setExcluded(res.data.excluded ?? 0); })
      .catch((e) => setError((e as Error).message || "Could not load the model list."));
  }, []);

  const selectedModel = models?.find((m) => m.id === selected);

  const shown = (models ?? []).filter((m) => {
    if (freeOnly && !m.isFree) return false;
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q);
  });

  if (error) {
    return (
      <div className="fg">
        <label>Model</label>
        <div style={{ fontSize: 12, color: "var(--red-t)", padding: "8px 0" }}>{error}</div>
        <div style={{ fontSize: 12, color: "var(--text2)" }}>
          You can still type a model id manually below — anything OpenRouter accepts will work.
        </div>
        <input className="fi" style={{ marginTop: 8 }} value={selected} placeholder="e.g. openai/gpt-4o-mini"
          onChange={(e) => onSelect({
            id: e.target.value, name: e.target.value, contextLength: 0,
            promptPer1M: 0, completionPer1M: 0, isFree: false, supportsJsonMode: false,
          })} />
      </div>
    );
  }

  if (!models) {
    return (
      <div className="fg">
        <label>Model</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", fontSize: 12, color: "var(--text2)" }}>
          <Spinner size={13} /> Loading OpenRouter's model catalog…
        </div>
      </div>
    );
  }

  return (
    <div className="fg">
      <label htmlFor="or-filter">Model</label>

      <div style={{ padding: "9px 12px", background: "var(--gray)", borderRadius: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: .4 }}>
          Selected
        </div>
        {selected ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--font-mono)", marginTop: 3, wordBreak: "break-all" }}>{selected}</div>
            {selectedModel ? (
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 3 }}>
                {formatPer1M(selectedModel.promptPer1M)} in / {formatPer1M(selectedModel.completionPer1M)} out per 1M tokens
                {" · "}{formatContext(selectedModel.contextLength)}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: "var(--orange-t)", marginTop: 3 }}>
                Not in the usable list — it may have been retired, or it can't return
                structured JSON. Pick another below.
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 3 }}>None yet — choose one below.</div>
        )}
      </div>

      <input id="or-filter" className="fi" placeholder={`Search ${models.length} models — try 'haiku', 'free', 'llama'…`}
        value={filter} onChange={(e) => setFilter(e.target.value)} />

      <div style={{ display: "flex", gap: 14, margin: "9px 2px 8px", fontSize: 12, color: "var(--text2)" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 5, fontWeight: 400, margin: 0 }}>
          <input type="checkbox" checked={freeOnly} onChange={(e) => setFreeOnly(e.target.checked)} style={{ width: "auto" }} />
          Free only
        </label>
        <span style={{ marginLeft: "auto" }}>{shown.length} of {models.length}</span>
      </div>

      <div role="listbox" aria-label="OpenRouter models" style={{
        maxHeight: 320, overflowY: "auto",
        border: "0.5px solid var(--border)", borderRadius: 8,
      }}>
        {shown.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: "var(--text2)", textAlign: "center" }}>
            No models match that search.
          </div>
        )}
        {shown.map((m) => {
          const active = m.id === selected;
          return (
            <div key={m.id} role="option" aria-selected={active} tabIndex={0}
              onClick={() => onSelect(m)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(m); } }}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "8px 11px",
                borderBottom: "0.5px solid var(--border)", cursor: "pointer",
                background: active ? "var(--blue-l)" : "transparent",
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--gray)"; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: active ? 600 : 500 }}>{m.name}</div>
                <div style={{ fontSize: 10.5, color: "var(--text3)", fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{m.id}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                {m.isFree ? (
                  <span className="badge" style={{ background: "var(--green-l)", color: "var(--green-t)", fontSize: 10 }}>free</span>
                ) : (
                  <div style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                    {formatPer1M(m.promptPer1M)} <span style={{ color: "var(--text3)" }}>in</span>
                    {" · "}
                    {formatPer1M(m.completionPer1M)} <span style={{ color: "var(--text3)" }}>out</span>
                  </div>
                )}
                <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 1 }}>
                  {formatContext(m.contextLength)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 7, lineHeight: 1.5 }}>
        Prices are USD per 1M tokens, live from OpenRouter, and exclude their fee. Triage
        sends a few hundred tokens per ticket, so the cheap end of the list goes a long way.
        {excluded > 0 && ` ${excluded} of OpenRouter's models are hidden because they can't return structured JSON, which triage and the follow-up questions depend on.`}
      </div>
    </div>
  );
}

function AiPanel() {
  const isAdmin = useIsAdmin();
  const defaults = INTEGRATION_DEFAULTS.ai.config as AiIntegrationConfig;
  const [cfg, setCfg] = useState<AiIntegrationConfig>(defaults);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getIntegration<AiIntegrationConfig>("ai").then((doc) => {
      const merged: AiIntegrationConfig = { ...defaults, ...doc.config };
      // Auto-upgrade deprecated Gemini models on load.
      if (merged.geminiModel && DEPRECATED_MODELS.has(merged.geminiModel)) merged.geminiModel = PROVIDER_MODELS.gemini[0].value;
      if (merged.triageModel && DEPRECATED_MODELS.has(merged.triageModel)) merged.triageModel = PROVIDER_MODELS.gemini[0].value;
      setCfg(merged);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    // Saving OpenRouter with no model would silently fall back to the
    // backend default, which is not what the admin picked.
    if (cfg.provider === "openrouter" && !cfg.openrouterModel) {
      toast("Choose an OpenRouter model first", "error");
      return;
    }
    setSaving(true);
    try {
      // Clamp before writing so a hand-typed value can't persist out of
      // range. The backend clamps again on read — this is UI hygiene.
      const rounds = Number(cfg.maxInfoRounds);
      const safeRounds = Number.isFinite(rounds)
        ? Math.min(MAX_MAX_INFO_ROUNDS, Math.max(MIN_MAX_INFO_ROUNDS, Math.floor(rounds)))
        : DEFAULT_MAX_INFO_ROUNDS;
      const next = { ...cfg, maxInfoRounds: safeRounds };
      await saveIntegrationConfig<AiIntegrationConfig>("ai", next);
      setCfg(next);
      toast("AI settings saved");
    } catch { toast("Failed to save AI settings", "error"); }
    setSaving(false);
  };

  type ModelKey = "geminiModel" | "openaiModel" | "anthropicModel" | "openrouterModel";

  const onProviderChange = (provider: AiProviderId) => {
    const key = `${provider}Model` as ModelKey;
    const currentModel = cfg[key];
    // OpenRouter has no static list — leave the model blank until the
    // admin picks one from the catalog rather than inventing a default.
    const defaultModel = PROVIDER_MODELS[provider][0]?.value ?? "";
    const next = currentModel || defaultModel;
    setCfg((c) => ({ ...c, provider, [key]: next, triageModel: next }));
  };

  const onOpenRouterSelect = (m: OpenRouterModel) => {
    setCfg((c) => ({
      ...c,
      openrouterModel: m.id,
      triageModel: m.id,
      // Recorded so the backend knows whether it may send response_format —
      // sending it to a model that doesn't support it is a hard 400.
      openrouterJsonMode: m.supportsJsonMode,
    }));
  };

  const modelKey = `${cfg.provider}Model` as ModelKey;
  const currentModel = cfg[modelKey] || PROVIDER_MODELS[cfg.provider][0]?.value || "";

  return (
    <>
      <SCard title="AI Provider" subtitle="Choose which provider powers triage and chat. Switching takes effect on the next ticket."
        icon="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z"
        iconBg="var(--gem-l)" iconColor="var(--gem)"
      >
        <div className="fg">
          <label>Provider</label>
          <select className="fsel" value={cfg.provider} onChange={(e) => onProviderChange(e.target.value as AiProviderId)}>
            <option value="gemini">Gemini (Google)</option>
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic Claude</option>
            <option value="openrouter">OpenRouter (hundreds of models)</option>
          </select>
          <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 6 }}>
            Switching provider takes effect immediately — keys are read from Secret Manager at
            request time, so no redeploy is needed.
          </div>
        </div>
        {cfg.provider === "openrouter" ? (
          <OpenRouterModelPicker selected={cfg.openrouterModel ?? ""} onSelect={onOpenRouterSelect} />
        ) : (
          <div className="fg">
            <label>Model</label>
            <select className="fsel" value={currentModel}
              onChange={(e) => setCfg((c) => ({ ...c, [modelKey]: e.target.value, triageModel: e.target.value }))}>
              {PROVIDER_MODELS[cfg.provider].map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        )}
        <div style={{ fontSize: 12, color: "var(--text2)", padding: "6px 0" }}>
          Required secret: <code>{cfg.provider.toUpperCase()}_API_KEY</code> — set in the API Keys panel.
        </div>
      </SCard>

      <SCard title="Triage Behavior" subtitle="How AI handles incoming tickets"
        icon="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z"
        iconBg="var(--blue-l)" iconColor="var(--blue)"
      >
        <div className="fg">
          <label>Auto-assign confidence threshold ({cfg.autoAssignThreshold}%)</label>
          <input type="range" min={50} max={99} value={cfg.autoAssignThreshold}
            onChange={(e) => setCfg((c) => ({ ...c, autoAssignThreshold: Number(e.target.value) }))} style={{ width: "100%" }} />
        </div>
        <ToggleRow title="Send AI triage summary to user on submission" value={cfg.sendSummaryToUser} onChange={(v) => setCfg((c) => ({ ...c, sendSummaryToUser: v }))} />
        <ToggleRow title="AI self-service deflection before ticket creation" value={cfg.selfServiceDeflection} onChange={(v) => setCfg((c) => ({ ...c, selfServiceDeflection: v }))} />
        <div className="fg">
          <label htmlFor="max-info-rounds">Info-gathering rounds before human escalation</label>
          <input id="max-info-rounds" className="fi" type="number"
            min={MIN_MAX_INFO_ROUNDS} max={MAX_MAX_INFO_ROUNDS} step={1}
            value={cfg.maxInfoRounds}
            aria-describedby="max-info-rounds-help"
            onChange={(e) => setCfg((c) => ({ ...c, maxInfoRounds: e.target.value === "" ? DEFAULT_MAX_INFO_ROUNDS : Number(e.target.value) }))}
            style={{ maxWidth: 120 }} />
          <div id="max-info-rounds-help" style={{ fontSize: 12, color: "var(--text2)", marginTop: 6 }}>
            When a ticket lacks detail, the AI replies asking the submitter for more.
            After {cfg.maxInfoRounds} {cfg.maxInfoRounds === 1 ? "unanswered round" : "rounds"} without
            enough information, it stops asking, posts an escalation note, and hands the ticket
            to a human agent. Allowed range {MIN_MAX_INFO_ROUNDS}–{MAX_MAX_INFO_ROUNDS}; tickets
            already waiting on a submitter keep the limit they started with.
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? <Spinner size={12} color="white" /> : "Save"}
            </button>
          </div>
        )}
      </SCard>
    </>
  );
}

// ─── SLA Rules Panel ──────────────────────────────────────────────

function SlaPanel() {
  const isAdmin = useIsAdmin();
  const [cfg, setCfg] = useState<SlaConfig>(DEFAULT_SLA);
  const [newHoliday, setNewHoliday] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getConfigDoc<SlaConfig>("sla").then((c) => {
      if (!c) return;
      setCfg({
        ...DEFAULT_SLA,
        ...c,
        businessSchedule: { ...DEFAULT_BUSINESS_SCHEDULE, ...(c.businessSchedule ?? {}) },
      });
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await setConfigDoc<SlaConfig>("sla", cfg);
      toast("SLA rules saved");
    } catch { toast("Failed to save SLA rules", "error"); }
    setSaving(false);
  };

  const ROWS = [
    { key: "P1" as const, label: "P1 Critical", bg: "var(--red-l)",    color: "var(--red-t)" },
    { key: "P2" as const, label: "P2 High",     bg: "var(--orange-l)", color: "var(--orange-t)" },
    { key: "P3" as const, label: "P3 Medium",   bg: "var(--blue-l)",   color: "var(--blue-t)" },
    { key: "P4" as const, label: "P4 Low",      bg: "var(--gray)",     color: "var(--text2)" },
  ];

  const sched = cfg.businessSchedule ?? DEFAULT_BUSINESS_SCHEDULE;
  const setSched = (next: Partial<BusinessSchedule>) =>
    setCfg((c) => ({ ...c, businessSchedule: { ...DEFAULT_BUSINESS_SCHEDULE, ...sched, ...next } }));

  const toggleDay = (d: number) => {
    const has = sched.workingDays.includes(d);
    const next = has ? sched.workingDays.filter((x) => x !== d) : [...sched.workingDays, d].sort();
    setSched({ workingDays: next });
  };

  const addHoliday = () => {
    const v = newHoliday.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) { toast("Use YYYY-MM-DD format", "error"); return; }
    if (sched.holidays.includes(v)) { toast("Already added", "error"); return; }
    setSched({ holidays: [...sched.holidays, v].sort() });
    setNewHoliday("");
  };

  const removeHoliday = (d: string) =>
    setSched({ holidays: sched.holidays.filter((x) => x !== d) });

  // Make sure the user's actual timezone is selectable even if not in the curated list.
  const tzOptions = Array.from(new Set([sched.timezone, ...COMMON_TIMEZONES])).filter(Boolean);

  return (
    <>
      <SCard title="SLA Rules" subtitle="Define response and resolution targets by priority (in hours)"
        icon="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
        iconBg="var(--blue-l)" iconColor="var(--blue)"
      >
        <div style={{ display: "flex", gap: 12, fontSize: 11, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .4, padding: "0 10px 6px" }}>
          <span style={{ minWidth: 100 }}>Priority</span>
          <span style={{ flex: 1 }}>Response (hours)</span>
          <span style={{ flex: 1 }}>Resolution (hours)</span>
        </div>
        {ROWS.map((s) => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: s.bg, borderRadius: "var(--rs)", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: s.color, minWidth: 100 }}>{s.label}</span>
            <input type="number" min={0} className="fi" style={{ flex: 1, fontSize: 12 }}
              value={cfg[s.key].responseHours}
              onChange={(e) => setCfg((c) => ({ ...c, [s.key]: { ...c[s.key], responseHours: Number(e.target.value) } }))} />
            <input type="number" min={0} className="fi" style={{ flex: 1, fontSize: 12 }}
              value={cfg[s.key].resolutionHours}
              onChange={(e) => setCfg((c) => ({ ...c, [s.key]: { ...c[s.key], resolutionHours: Number(e.target.value) } }))} />
          </div>
        ))}
      </SCard>

      <SCard title="Business Hours" subtitle="When enabled, SLA timers only count time during your working hours, days, and excluding holidays"
        icon="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm.5 11H7v-2h4V6h1.5v7z"
        iconBg="var(--green-l)" iconColor="var(--green)"
        badge={sched.enabled ? { label: "Active", type: "gem" } : { label: "24/7", type: "warn" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 4px 14px" }}>
          <Toggle value={sched.enabled} onChange={(v) => setSched({ enabled: v })} />
          <div style={{ fontSize: 13 }}>
            <div style={{ fontWeight: 500 }}>{sched.enabled ? "Business hours mode" : "24/7 mode"}</div>
            <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2 }}>
              {sched.enabled
                ? "SLA timers pause outside working hours, on non-working days, and on holidays."
                : "SLA timers run continuously regardless of day or hour."}
            </div>
          </div>
        </div>

        {sched.enabled && (
          <>
            <div className="frow" style={{ marginBottom: 14 }}>
              <div className="fg">
                <label>Timezone</label>
                <select className="fsel" value={sched.timezone}
                  onChange={(e) => setSched({ timezone: e.target.value })}>
                  {tzOptions.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
                <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 4 }}>
                  Working hours and holidays are interpreted in this timezone.
                </div>
              </div>
              <div className="fg">
                <label>Working hours</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="time" className="fi" style={{ flex: 1 }}
                    value={sched.workingHours.start}
                    onChange={(e) => setSched({ workingHours: { ...sched.workingHours, start: e.target.value } })} />
                  <span style={{ fontSize: 12, color: "var(--text2)" }}>to</span>
                  <input type="time" className="fi" style={{ flex: 1 }}
                    value={sched.workingHours.end}
                    onChange={(e) => setSched({ workingHours: { ...sched.workingHours, end: e.target.value } })} />
                </div>
              </div>
            </div>

            <div className="fg" style={{ marginBottom: 14 }}>
              <label>Working days</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {DAY_LABELS.map((label, idx) => {
                  const active = sched.workingDays.includes(idx);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      style={{
                        flex: 1,
                        minWidth: 60,
                        padding: "8px 0",
                        border: `1px solid ${active ? "var(--blue)" : "var(--border-med)"}`,
                        borderRadius: "var(--rs)",
                        background: active ? "var(--blue-l)" : "var(--surface)",
                        color: active ? "var(--blue-t)" : "var(--text)",
                        fontWeight: active ? 600 : 400,
                        fontSize: 12,
                        cursor: "pointer",
                        fontFamily: "var(--font)",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => setSched({ workingDays: [1, 2, 3, 4, 5] })}>M–F</button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => setSched({ workingDays: [1, 2, 3, 4, 5, 6] })}>M–Sat</button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => setSched({ workingDays: [0, 1, 2, 3, 4, 5, 6] })}>All days</button>
              </div>
            </div>

            <div className="fg">
              <label>Holidays <span style={{ color: "var(--text2)", fontWeight: 400 }}>— full days the helpdesk is closed</span></label>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input type="date" className="fi" style={{ flex: 1 }}
                  value={newHoliday}
                  onChange={(e) => setNewHoliday(e.target.value)} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={addHoliday}>+ Add</button>
              </div>
              {sched.holidays.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text2)", padding: "10px 12px", background: "var(--gray)", borderRadius: "var(--rs)" }}>
                  No holidays configured.
                </div>
              ) : (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {sched.holidays.map((d) => (
                    <span key={d} style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "5px 10px", background: "var(--blue-l)", color: "var(--blue-t)",
                      borderRadius: 999, fontSize: 12, fontWeight: 500,
                    }}>
                      {d}
                      <button type="button" onClick={() => removeHoliday(d)}
                        style={{ background: "none", border: "none", color: "var(--blue-t)", cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}
                        aria-label={`Remove ${d}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </SCard>

      {isAdmin && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? <Spinner size={12} color="white" /> : "Save SLA & Business Hours"}
          </button>
        </div>
      )}
    </>
  );
}

// ─── Email Panel ──────────────────────────────────────────────────

function EmailPanel() {
  const isAdmin = useIsAdmin();
  const defaults = INTEGRATION_DEFAULTS.email.config as EmailIntegrationConfig;
  const [cfg, setCfg] = useState<EmailIntegrationConfig>(defaults);
  const [enabled, setEnabled] = useState(INTEGRATION_DEFAULTS.email.enabled);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    getIntegration<EmailIntegrationConfig>("email").then((doc) => {
      setCfg({ ...defaults, ...doc.config });
      setEnabled(doc.enabled);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await saveIntegrationConfig<EmailIntegrationConfig>("email", cfg);
      toast("Email settings saved");
    } catch { toast("Failed to save email settings", "error"); }
    setSaving(false);
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast("Enter a recipient", "error"); return; }
    setTesting(true);
    try {
      const fn = httpsCallable(getFunctions(), "sendTestEmail");
      await fn({ to: testTo.trim() });
      toast(`Test email queued to ${testTo}`);
    } catch (e) {
      toast(`Test failed: ${(e as Error).message}`, "error");
    }
    setTesting(false);
  };

  return (
    <SCard title="Email Notifications" subtitle="Transactional email via Firebase Trigger Email extension"
      icon="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
      iconBg="var(--blue-l)" iconColor="var(--blue)"
      badge={enabled ? { label: "Enabled", type: "success" } : { label: "Disabled", type: "warn" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0 14px", borderBottom: "0.5px solid var(--border)", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Email integration enabled</div>
          <div style={{ fontSize: 12, color: "var(--text2)" }}>Turn off to skip email sends entirely.</div>
        </div>
        <Toggle value={enabled} onChange={async (v) => { setEnabled(v); await setIntegrationEnabled("email", v); }} />
      </div>
      <div className="fg">
        <label>Provider</label>
        <select className="fsel" value={cfg.provider || "resend"}
          onChange={(e) => setCfg((c) => ({ ...c, provider: e.target.value as EmailProviderId }))}>
          <option value="resend">Resend (recommended — single API key, fastest setup)</option>
          <option value="sendgrid">SendGrid (alternative API key)</option>
          <option value="smtp">SMTP via Firebase Trigger Email Extension (advanced)</option>
        </select>
        <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 4 }}>
          {cfg.provider === "smtp"
            ? "Requires the Trigger Email extension installed and configured with SMTP credentials."
            : `Set ${(cfg.provider || "resend") === "resend" ? "RESEND_API_KEY" : "SENDGRID_API_KEY"} in the API Keys panel — the panel has step-by-step signup instructions.`}
        </div>
      </div>
      <div className="fg"><label>From Address</label>
        <input className="fi" value={cfg.fromAddress} placeholder={(cfg.provider || "resend") === "resend" ? "onboarding@resend.dev (default) or your verified domain" : "helpdesk@company.com"}
          onChange={(e) => setCfg((c) => ({ ...c, fromAddress: e.target.value }))} />
      </div>
      <div className="fg"><label>Reply-To</label>
        <input className="fi" value={cfg.replyTo} placeholder="it-support@company.com"
          onChange={(e) => setCfg((c) => ({ ...c, replyTo: e.target.value }))} />
      </div>
      <ToggleRow title="Ticket confirmation to submitter" value={cfg.ticketConfirmation} onChange={(v) => setCfg((c) => ({ ...c, ticketConfirmation: v }))} />
      <ToggleRow title="Agent assignment notification" value={cfg.agentAssignment} onChange={(v) => setCfg((c) => ({ ...c, agentAssignment: v }))} />
      <ToggleRow title="CSAT survey on resolution" value={cfg.csatSurvey} onChange={(v) => setCfg((c) => ({ ...c, csatSurvey: v }))} />
      {isAdmin && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? <Spinner size={12} color="white" /> : "Save"}
          </button>
        </div>
      )}

      <details style={{ borderTop: "0.5px solid var(--border)", marginTop: 16, paddingTop: 14 }}>
        <summary style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", cursor: "pointer", userSelect: "none" }}>
          Inbound email — receive tickets via email (advanced, optional)
        </summary>
        <div style={{ fontSize: 11, color: "var(--text2)", lineHeight: 1.55, marginTop: 10 }}>
          To accept tickets sent to e.g. <code>support@yourdomain.com</code>, point an email-receiving service at the helpdesk's <code>inboundEmail</code> Cloud Function. Resend, Postmark, and SendGrid all work — pick whichever fits your setup.
          <pre style={{ background: "var(--gray)", padding: "6px 8px", borderRadius: 4, marginTop: 8, marginBottom: 8, fontSize: 11, whiteSpace: "pre-wrap", overflowX: "auto" }}>https://&lt;region&gt;-&lt;project&gt;.cloudfunctions.net/inboundEmail</pre>
          Get the exact URL from Firebase Console → Functions → inboundEmail. The function auto-detects whether the POST is JSON (Postmark, Resend Inbound) or multipart form data (SendGrid Inbound Parse) and parses accordingly.
        </div>
        <div style={{ fontSize: 11, color: "var(--text2)", lineHeight: 1.55, marginTop: 8 }}>
          <strong>Optional security:</strong> set a shared secret as <code>INBOUND_EMAIL_SECRET</code> in API Keys. The webhook then must include header <code>x-helpdesk-inbound-secret</code> matching that value, or POSTs are rejected with 401.
        </div>
      </details>

      <div style={{ borderTop: "0.5px solid var(--border)", marginTop: 16, paddingTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 6 }}>
          Test outgoing email
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input className="fi" type="email" placeholder="recipient@example.com"
            value={testTo} onChange={(e) => setTestTo(e.target.value)} style={{ flex: 1 }} />
          <button className="btn btn-secondary btn-sm" onClick={sendTest} disabled={testing}>
            {testing ? <Spinner size={12} /> : "Send Test"}
          </button>
        </div>
        <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 6, lineHeight: 1.5 }}>
          {cfg.provider === "smtp" ? (
            <>Outgoing email requires the <strong>Firebase Trigger Email</strong> extension installed on this project.
            Install at <a href="https://console.firebase.google.com/project/_/extensions/install?ref=firebase/firestore-send-email" target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>Firebase Console → Extensions</a>,
            configure SMTP credentials, and point the watched collection at <code>mail</code>.</>
          ) : (
            <>The selected provider sends via API directly. No extension needed — just paste the API key in the API Keys panel.</>
          )}
        </div>
      </div>
    </SCard>
  );
}

// ─── CSV Import Panel ─────────────────────────────────────────────

interface ImportResult {
  created: number;
  updated: number;
  disabled: number;
  unchanged: number;
  errors: string[];
}

function CsvPanel() {
  const isAdmin = useIsAdmin();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [opts, setOpts] = useState({ updateExisting: true, disableInactive: true, sendWelcome: true, dryRun: false });
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => { setCsvFile(file); setResult(null); };

  const runImport = async () => {
    if (!csvFile) return;
    setImporting(true);
    setProgress(10);
    try {
      const path = `imports/users/${Date.now()}.csv`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, csvFile);
      setProgress(50);

      const fn = httpsCallable<unknown, ImportResult>(getFunctions(), "csvImportUsers");
      const res = await fn({ storagePath: path, options: opts });
      setProgress(100);
      setResult(res.data);
      toast(`Import complete — ${res.data.created} created, ${res.data.updated} updated, ${res.data.disabled} disabled`);
    } catch (e) {
      toast(`Import failed: ${(e as Error).message ?? "function not deployed yet"}`, "error");
    }
    setImporting(false);
    setCsvFile(null);
    setProgress(0);
  };

  const downloadTemplate = () => {
    const csv = "email,first_name,last_name,role,department,status\nexample@company.com,First,Last,user,Engineering,active";
    const a = document.createElement("a");
    a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = "helpdesk-users-template.csv";
    a.click();
    toast("Template downloaded");
  };

  return (
    <>
      <SCard title="Upload CSV File" subtitle="Supports create, update, and deactivate in one pass"
        icon="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"
        iconBg="var(--green-l)" iconColor="var(--green-t)"
        badge={csvFile ? { label: csvFile.name, type: "gem" } : { label: "No file", type: "warn" }}
      >
        {!importing && !csvFile && (
          <>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
              style={{ border: "2px dashed var(--border-med)", borderRadius: "var(--r)", padding: 20, textAlign: "center", cursor: "pointer", background: "var(--gray)" }}
            >
              <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: "none" }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
              <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Drop CSV file here or click to browse</div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>.csv files only · Max 5,000 rows</div>
            </div>
            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)" }}>Required CSV format</span>
                <button className="btn btn-secondary btn-sm" onClick={downloadTemplate}>↓ Download Template</button>
              </div>
              <pre style={{ fontSize: 11, background: "var(--gray)", border: "1px solid var(--border-med)", borderRadius: 6, padding: "10px 12px", color: "var(--text2)", overflowX: "auto", fontFamily: "var(--font-mono)" }}>
{`email,first_name,last_name,role,department,status
sarah@company.com,Sarah,Mitchell,user,Engineering,active
tom@company.com,Tom,Kelly,agent,IT,active
old@company.com,Old,Employee,user,Sales,inactive`}
              </pre>
            </div>
          </>
        )}

        {csvFile && !importing && (
          <>
            <div style={{ background: "var(--blue-l)", border: "1px solid rgba(0,113,227,.2)", borderRadius: "var(--rs)", padding: "10px 12px", fontSize: 12, color: "var(--blue-t)", marginBottom: 10 }}>
              <strong>📄 {csvFile.name}</strong> · {(csvFile.size / 1024).toFixed(1)} KB · ready to import
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setCsvFile(null)}>Cancel</button>
              {isAdmin && <button className="btn btn-primary btn-sm" onClick={runImport}>{opts.dryRun ? "Dry Run" : "Import Users"}</button>}
            </div>
          </>
        )}

        {importing && (
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
              {progress < 50 ? "Uploading…" : "Processing…"} {progress}%
            </div>
            <div className="pbar"><div className="pfill" style={{ width: `${progress}%`, background: "var(--blue)" }} /></div>
          </div>
        )}

        {result && (
          <div style={{ marginTop: 12, padding: 12, background: "var(--green-l)", borderRadius: "var(--rs)", fontSize: 12, color: "var(--green-t)" }}>
            <strong>✓ Import complete</strong> — {result.created} created, {result.updated} updated, {result.disabled} disabled, {result.unchanged} unchanged
            {result.errors.length > 0 && (
              <div style={{ marginTop: 6, color: "var(--red-t)" }}>
                <strong>{result.errors.length} errors:</strong>
                <ul style={{ marginTop: 4, marginLeft: 16 }}>
                  {result.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </SCard>

      <SCard title="Import Behavior" subtitle="How conflicts and missing users are handled"
        icon="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
        iconBg="var(--blue-l)" iconColor="var(--blue)"
      >
        <ToggleRow title="Update existing users" subtitle="If email matches, overwrite name, role, and department" value={opts.updateExisting} onChange={(v) => setOpts((o) => ({ ...o, updateExisting: v }))} />
        <ToggleRow title="Disable users marked inactive" subtitle="Rows with status=inactive will be deactivated, not deleted" value={opts.disableInactive} onChange={(v) => setOpts((o) => ({ ...o, disableInactive: v }))} />
        <ToggleRow title="Send welcome email to new users" subtitle="New accounts receive a welcome email with login instructions" value={opts.sendWelcome} onChange={(v) => setOpts((o) => ({ ...o, sendWelcome: v }))} />
        <div style={{ borderBottom: "none" }}>
          <ToggleRow title="Dry run mode" subtitle="Preview all changes without writing to the database" value={opts.dryRun} onChange={(v) => setOpts((o) => ({ ...o, dryRun: v }))} />
        </div>
      </SCard>
    </>
  );
}

// ─── Google Workspace Panel ───────────────────────────────────────

function GWSPanel() {
  const isAdmin = useIsAdmin();
  const defaults = INTEGRATION_DEFAULTS.gws.config as GwsIntegrationConfig;
  const [cfg, setCfg] = useState<GwsIntegrationConfig>(defaults);
  const [enabled, setEnabled] = useState(false);
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [keyFileName, setKeyFileName] = useState("");
  const keyRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getIntegration<GwsIntegrationConfig>("gws").then((doc) => {
      setCfg({ ...defaults, ...doc.config });
      setEnabled(doc.enabled);
    });
    getSyncLogs(5).then(setLogs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleEnabled = async (v: boolean) => {
    setEnabled(v);
    await setIntegrationEnabled("gws", v);
    toast(v ? "Google Workspace enabled" : "Google Workspace disabled");
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveIntegrationConfig<GwsIntegrationConfig>("gws", cfg);
      toast("Google Workspace settings saved");
    } catch { toast("Failed to save settings", "error"); }
    setSaving(false);
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const fn = httpsCallable(getFunctions(), "gwsSyncManual");
      await fn({});
      toast("Sync complete");
      const fresh = await getSyncLogs(5);
      setLogs(fresh);
    } catch (e) {
      toast(`Sync failed: ${(e as Error).message ?? "function not deployed yet"}`, "error");
    }
    setSyncing(false);
  };

  const dotColor = { success: "var(--green)", warning: "var(--orange)", failed: "var(--red)" };

  return (
    <>
      <SCard title="Google Workspace" subtitle="Optional identity provider — sync users and enable Google SSO"
        icon="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"
        iconBg="var(--red-l)" iconColor="#ea4335"
        badge={enabled ? { label: "Enabled", type: "success" } : { label: "Disabled", type: "warn" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0 14px", borderBottom: "0.5px solid var(--border)", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Google Workspace integration enabled</div>
            <div style={{ fontSize: 12, color: "var(--text2)" }}>When off, scheduled directory sync stops. Google SSO availability is controlled separately via Firebase Auth.</div>
          </div>
          <Toggle value={enabled} onChange={toggleEnabled} />
        </div>

        {enabled && (
          <>
            <div style={{ background: "var(--blue-l)", border: "1px solid rgba(0,113,227,.2)", borderRadius: "var(--rs)", padding: "10px 12px", fontSize: 12, color: "var(--blue-t)", marginBottom: 14, lineHeight: 1.5 }}>
              <strong>Setup:</strong> Service account JSON key goes in the <strong>API Keys</strong> panel as <code>GWS_SERVICE_ACCOUNT_KEY</code>. The fields below save non-secret config only.
            </div>
            <div className="frow" style={{ marginBottom: 12 }}>
              <div className="fg"><label>Customer Domain</label>
                <input className="fi" placeholder="example.com" value={cfg.customerDomain}
                  onChange={(e) => setCfg((c) => ({ ...c, customerDomain: e.target.value }))} />
              </div>
              <div className="fg"><label>Customer ID</label>
                <input className="fi" placeholder="C0xxxxxxx" value={cfg.customerId}
                  onChange={(e) => setCfg((c) => ({ ...c, customerId: e.target.value }))} />
              </div>
            </div>
            <div className="fg" style={{ marginBottom: 12 }}>
              <label>Service Account Email</label>
              <input className="fi" placeholder="helpdesk@project.iam.gserviceaccount.com" value={cfg.serviceAccountEmail}
                onChange={(e) => setCfg((c) => ({ ...c, serviceAccountEmail: e.target.value }))} />
            </div>
            <div className="fg" style={{ marginBottom: 12 }}>
              <label>Allowed Domain for Google SSO</label>
              <input className="fi" placeholder="example.com" value={cfg.allowedDomain}
                onChange={(e) => setCfg((c) => ({ ...c, allowedDomain: e.target.value }))} />
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 4 }}>
                Login page restricts Google sign-in to this domain. Leave blank to allow any Google account.
              </div>
            </div>
            <div className="fg" style={{ marginBottom: 14 }}>
              <label>Service Account Key Filename (informational)</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input className="fi" style={{ flex: 1, cursor: "pointer" }} placeholder="for your reference only" value={keyFileName} readOnly onClick={() => keyRef.current?.click()} />
                <button className="btn btn-secondary btn-sm" onClick={() => keyRef.current?.click()}>Browse</button>
                <input ref={keyRef} type="file" accept=".json" style={{ display: "none" }} onChange={(e) => setKeyFileName(e.target.files?.[0]?.name ?? "")} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={runSync} disabled={syncing}>
                {syncing ? <Spinner size={12} color="var(--text)" /> : null} Run Sync Now
              </button>
            </div>
          </>
        )}
      </SCard>

      {enabled && (
      <SCard title="Daily Sync Schedule" subtitle="Automatic nightly sync with change detection"
        icon="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
        iconBg="var(--blue-l)" iconColor="var(--blue)"
      >
        <div className="frow" style={{ marginBottom: 14 }}>
          <div className="fg">
            <label>Sync Time</label>
            <select className="fsel" value={cfg.syncTime} onChange={(e) => setCfg((c) => ({ ...c, syncTime: e.target.value }))}>
              <option value="02:00">2:00 AM — Daily</option>
              <option value="03:00">3:00 AM — Daily</option>
              <option value="06:00">6:00 AM — Daily</option>
              <option value="every-6h">Every 6 hours</option>
              <option value="every-12h">Every 12 hours</option>
            </select>
          </div>
          <div className="fg">
            <label>Source Group (optional)</label>
            <input className="fi" placeholder="helpdesk-users@company.com" value={cfg.targetGroup ?? ""}
              onChange={(e) => setCfg((c) => ({ ...c, targetGroup: e.target.value }))} />
          </div>
        </div>
        <ToggleRow title="Auto-disable suspended users" subtitle="When suspended in GWS, disable their helpdesk account" value={cfg.autoDisableSuspended} onChange={(v) => setCfg((c) => ({ ...c, autoDisableSuspended: v }))} />
        <ToggleRow title="Auto-delete removed users after 30 days" subtitle="Hard-delete accounts gone from GWS for 30+ days" value={cfg.autoDeleteRemoved} onChange={(v) => setCfg((c) => ({ ...c, autoDeleteRemoved: v }))} />
        <ToggleRow title="Sync org units as departments" subtitle="Map GWS OUs to helpdesk departments automatically" value={cfg.syncOrgUnits} onChange={(v) => setCfg((c) => ({ ...c, syncOrgUnits: v }))} />
        <div style={{ borderBottom: "none" }}>
          <ToggleRow title="Notify admin on sync errors" subtitle="Send Slack or email alert when sync fails" value={cfg.notifyOnErrors} onChange={(v) => setCfg((c) => ({ ...c, notifyOnErrors: v }))} />
        </div>
        {isAdmin && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? <Spinner size={12} color="white" /> : "Save Settings"}
            </button>
          </div>
        )}
      </SCard>
      )}

      <SCard title="Sync History" subtitle={logs.length ? `Last ${logs.length} sync runs` : "No sync runs yet"}
        icon="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"
        iconBg="var(--gray)" iconColor="var(--text2)"
      >
        {logs.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--text2)", padding: "8px 0" }}>
            No sync runs recorded yet. Click "Run Sync Now" above to trigger one.
          </div>
        )}
        {logs.map((log, i) => (
          <div key={log.id} style={{ display: "flex", gap: 10, position: "relative", paddingBottom: 12 }}>
            {i < logs.length - 1 && <div style={{ position: "absolute", left: 11, top: 24, width: 1, height: "calc(100% - 8px)", background: "var(--border)" }} />}
            <div style={{ width: 22, height: 22, borderRadius: "50%", background: dotColor[log.status], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "white", fontWeight: 700, flexShrink: 0 }}>
              {log.status === "success" ? "✓" : log.status === "warning" ? "!" : "✕"}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>
                {log.runAt.toDate().toLocaleString()}
                {log.status !== "success" && (
                  <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: log.status === "warning" ? "var(--orange-l)" : "var(--red-l)", color: log.status === "warning" ? "var(--orange-t)" : "var(--red-t)" }}>
                    {log.status}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 1 }}>
                {log.totalUsers} users · {log.created} created · {log.updated} updated · {log.disabled} disabled · {log.durationMs}ms
              </div>
            </div>
          </div>
        ))}
      </SCard>
    </>
  );
}

// ─── Slack Panel ──────────────────────────────────────────────────

function SlackPanel() {
  const isAdmin = useIsAdmin();
  const defaults = INTEGRATION_DEFAULTS.slack.config as SlackIntegrationConfig;
  const [cfg, setCfg] = useState<SlackIntegrationConfig>(defaults);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    getIntegration<SlackIntegrationConfig>("slack").then((doc) => {
      setCfg({
        ...defaults,
        ...doc.config,
        channels: { ...defaults.channels, ...doc.config.channels },
        triggers: { ...defaults.triggers, ...doc.config.triggers },
      });
      setEnabled(doc.enabled);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleEnabled = async (v: boolean) => {
    setEnabled(v);
    await setIntegrationEnabled("slack", v);
    toast(v ? "Slack integration enabled" : "Slack integration disabled");
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveIntegrationConfig<SlackIntegrationConfig>("slack", cfg);
      toast("Slack settings saved");
    } catch { toast("Failed to save Slack settings", "error"); }
    setSaving(false);
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const fn = httpsCallable(getFunctions(), "slackSendTestMessage");
      await fn({ channel: cfg.channels.allTickets });
      toast(`Test message sent to ${cfg.channels.allTickets}`);
    } catch (e) {
      toast(`Test failed: ${(e as Error).message ?? "function not deployed yet"}`, "error");
    }
    setTesting(false);
  };

  const CHANNEL_ROWS = [
    { label: "P1 Critical tickets", key: "criticalTickets" as const },
    { label: "All new tickets",     key: "allTickets" as const },
    { label: "SLA breach alerts",   key: "slaAlerts" as const },
    { label: "Sync error alerts",   key: "syncErrors" as const },
    { label: "Daily digest report", key: "dailyDigest" as const },
  ];

  return (
    <>
      <SCard title="Slack" subtitle="Optional notification provider — post ticket alerts to your workspace"
        icon="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z"
        iconBg="var(--slack-l, #f4eef4)" iconColor="#4a154b"
        badge={enabled ? { label: "Enabled", type: "success" } : { label: "Disabled", type: "warn" }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0 14px" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Slack integration enabled</div>
            <div style={{ fontSize: 12, color: "var(--text2)" }}>Bot token (<code>SLACK_BOT_TOKEN</code>) is set in the API Keys panel.</div>
          </div>
          <Toggle value={enabled} onChange={toggleEnabled} />
        </div>
      </SCard>

      {enabled && (
      <>
      <SCard title="Channel Routing" subtitle="Send different notification types to different channels"
        icon="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"
        iconBg="var(--slack-l, #f4eef4)" iconColor="#4a154b"
      >
        <div style={{ marginBottom: 14 }}>
          {CHANNEL_ROWS.map((row) => (
            <div key={row.key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "0.5px solid var(--border)" }}>
              <span style={{ color: "var(--text3)", fontSize: 14, fontWeight: 700 }}>#</span>
              <span style={{ flex: 1, fontSize: 13 }}>{row.label}</span>
              <input className="fi" style={{ width: 160, fontSize: 12, padding: "5px 8px" }}
                value={cfg.channels[row.key]}
                onChange={(e) => setCfg((c) => ({ ...c, channels: { ...c.channels, [row.key]: e.target.value } }))} />
            </div>
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", marginBottom: 8 }}>Notification triggers</div>
        <ToggleRow title="New ticket created" subtitle="Post to #it-helpdesk when any ticket is submitted" value={cfg.triggers.newTicket} onChange={(v) => setCfg((c) => ({ ...c, triggers: { ...c.triggers, newTicket: v } }))} />
        <ToggleRow title="P1 critical escalation" subtitle="Immediate alert to #it-critical with @channel mention" value={cfg.triggers.p1Escalation} onChange={(v) => setCfg((c) => ({ ...c, triggers: { ...c.triggers, p1Escalation: v } }))} />
        <ToggleRow title="SLA breach warning (30 min)" subtitle="Alert assigned agent when SLA is 30 min from breach" value={cfg.triggers.slaWarning} onChange={(v) => setCfg((c) => ({ ...c, triggers: { ...c.triggers, slaWarning: v } }))} />
        <ToggleRow title="Ticket resolved" subtitle="Post resolution with CSAT score when available" value={cfg.triggers.ticketResolved} onChange={(v) => setCfg((c) => ({ ...c, triggers: { ...c.triggers, ticketResolved: v } }))} />
        <ToggleRow title="Daily digest (8 AM Mon–Fri)" subtitle="Queue summary: open tickets, SLA status, agent load" value={cfg.triggers.dailyDigest} onChange={(v) => setCfg((c) => ({ ...c, triggers: { ...c.triggers, dailyDigest: v } }))} />
        <div style={{ borderBottom: "none" }}>
          <ToggleRow title="GWS sync errors" subtitle="Alert #it-ops when Google Workspace sync fails" value={cfg.triggers.syncErrors} onChange={(v) => setCfg((c) => ({ ...c, triggers: { ...c.triggers, syncErrors: v } }))} />
        </div>

        {isAdmin && (
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button className="btn btn-secondary btn-sm" onClick={sendTest} disabled={testing}>
              {testing ? <Spinner size={12} color="var(--text)" /> : null} Send Test Message
            </button>
            <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
              {saving ? <Spinner size={12} color="white" /> : "Save Settings"}
            </button>
          </div>
        )}
      </SCard>

      </>
      )}

      {/* Message preview — show even when disabled, as documentation */}
      <SCard title="Message Format Preview" subtitle="How ticket alerts appear in Slack"
        icon="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"
        iconBg="var(--gray)" iconColor="var(--text2)"
      >
        <div style={{ background: "#1a1d21", borderRadius: "var(--r)", padding: 14, fontFamily: "var(--font-mono)", fontSize: 12, color: "#d1d2d3" }}>
          <div style={{ color: "#72767d", fontSize: 11, marginBottom: 8 }}>helpdeskbot  APP  2:03 PM</div>
          <div style={{ borderLeft: "4px solid #e74c3c", paddingLeft: 10, background: "rgba(255,255,255,.04)", borderRadius: "0 4px 4px 0", padding: "10px 10px 10px 14px" }}>
            <div style={{ color: "#ff6b6b", fontWeight: 700, fontSize: 13 }}>🔴 P1 Critical — INC-0042</div>
            <div style={{ color: "white", margin: "4px 0", fontSize: 13 }}>VPN not connecting after Windows update</div>
            <div style={{ color: "#72767d", fontSize: 11 }}>Sarah Mitchell · Network · Assigned to Tom K.</div>
            <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
              <span style={{ background: "#0071e3", color: "white", padding: "3px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer" }}>View Ticket</span>
              <span style={{ background: "rgba(255,255,255,.1)", color: "#d1d2d3", padding: "3px 10px", borderRadius: 4, fontSize: 11, cursor: "pointer" }}>Claim</span>
            </div>
          </div>
        </div>
      </SCard>
    </>
  );
}

// ─── Manual Users Panel ───────────────────────────────────────────

function UsersPanel() {
  const isAdmin = useIsAdmin();
  const [users, setUsers] = useState<HelpDeskUser[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", displayName: "", role: "user" as UserRole, department: "", password: "", sendWelcomeEmail: true });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, "users"), where("status", "in", ["active", "inactive"])), (snap) => {
      setUsers(snap.docs.map((d) => ({ uid: d.id, ...d.data() } as HelpDeskUser)));
    });
    return unsub;
  }, []);

  const create = async () => {
    setCreating(true);
    try {
      const fn = httpsCallable<typeof newUser, { uid: string; tempPassword?: string }>(getFunctions(), "createManualUser");
      const res = await fn(newUser);
      if (res.data.tempPassword) {
        toast(`Created ${newUser.email} — temp password: ${res.data.tempPassword}`);
      } else {
        toast(`Created ${newUser.email}`);
      }
      setShowAdd(false);
      setNewUser({ email: "", displayName: "", role: "user", department: "", password: "", sendWelcomeEmail: true });
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`, "error");
    }
    setCreating(false);
  };

  const callUserAction = async (action: string, uid: string, extra: Record<string, unknown> = {}) => {
    try {
      const fn = httpsCallable(getFunctions(), action);
      await fn({ uid, ...extra });
      toast("Done");
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`, "error");
    }
  };

  return (
    <>
      <SCard title="Manual Users" subtitle="Create users with email + password. Always available — no Workspace required."
        icon="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
        iconBg="var(--blue-l)" iconColor="var(--blue)"
        badge={{ label: `${users.length} users`, type: "gem" }}
      >
        {isAdmin && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(!showAdd)}>
              {showAdd ? "Cancel" : "+ Add User"}
            </button>
          </div>
        )}

        {showAdd && (
          <div style={{ background: "var(--gray)", borderRadius: 8, padding: 14, marginBottom: 14 }}>
            <div className="frow">
              <div className="fg"><label>Email</label>
                <input className="fi" type="email" value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
              </div>
              <div className="fg"><label>Display Name</label>
                <input className="fi" value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })} />
              </div>
            </div>
            <div className="frow">
              <div className="fg"><label>Role</label>
                <select className="fsel" value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}>
                  <option value="user">User</option>
                  <option value="agent">Agent</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="fg"><label>Department</label>
                <input className="fi" value={newUser.department}
                  onChange={(e) => setNewUser({ ...newUser, department: e.target.value })} />
              </div>
            </div>
            <div className="fg">
              <label>Password (leave blank to auto-generate and email)</label>
              <input className="fi" type="text" value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="optional — at least 8 characters" />
            </div>
            <ToggleRow title="Send welcome email with password reset link"
              value={newUser.sendWelcomeEmail}
              onChange={(v) => setNewUser({ ...newUser, sendWelcomeEmail: v })} />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button className="btn btn-primary btn-sm" disabled={creating || !newUser.email || !newUser.displayName} onClick={create}>
                {creating ? <Spinner size={12} color="white" /> : "Create User"}
              </button>
            </div>
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--gray)" }}>
                {["Email", "Name", "Role", "Department", "Status", "Source", ...(isAdmin ? ["Actions"] : [])].map((h) => (
                  <th key={h} style={{ fontSize: 10, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: .3, padding: "8px 10px", textAlign: "left", borderBottom: "0.5px solid var(--border)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.uid} style={{ borderBottom: "0.5px solid var(--border)" }}>
                  <td style={{ padding: "9px 10px", fontFamily: "var(--font-mono)", fontSize: 12 }}>{u.email}</td>
                  <td style={{ padding: "9px 10px" }}>{u.displayName}</td>
                  <td style={{ padding: "9px 10px" }}>
                    {isAdmin ? (
                      <select className="fsel" style={{ fontSize: 12, padding: "3px 6px" }} value={u.role}
                        onChange={(e) => callUserAction("changeUserRole", u.uid, { role: e.target.value })}>
                        <option value="user">User</option>
                        <option value="agent">Agent</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span style={{ textTransform: "capitalize" }}>{u.role}</span>
                    )}
                  </td>
                  <td style={{ padding: "9px 10px", color: "var(--text2)" }}>{u.department || "—"}</td>
                  <td style={{ padding: "9px 10px" }}>
                    <span className="badge" style={{
                      background: u.status === "active" ? "var(--green-l)" : "var(--orange-l)",
                      color: u.status === "active" ? "var(--green-t)" : "var(--orange-t)",
                    }}>{u.status}</span>
                  </td>
                  <td style={{ padding: "9px 10px", color: "var(--text2)", fontSize: 11 }}>{u.source}</td>
                  {isAdmin && (
                    <td style={{ padding: "9px 10px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => callUserAction("resetUserPassword", u.uid)}>Reset PW</button>
                        {u.status === "active" ? (
                          <button className="btn btn-secondary btn-sm" style={{ fontSize: 11 }} onClick={() => callUserAction("disableUser", u.uid)}>Disable</button>
                        ) : (
                          <button className="btn btn-green btn-sm" style={{ fontSize: 11 }} onClick={() => callUserAction("enableUser", u.uid)}>Enable</button>
                        )}
                        <button className="btn btn-danger btn-sm" style={{ fontSize: 11 }} onClick={() => {
                          if (confirm(`Delete ${u.email}? This is permanent.`)) callUserAction("deleteUser", u.uid);
                        }}>Delete</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} style={{ padding: 20, textAlign: "center", color: "var(--text2)" }}>
                  {isAdmin ? 'No users yet — click "Add User" to create one.' : "No users yet."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SCard>
    </>
  );
}

// ─── Integrations Overview Panel ──────────────────────────────────

function IntegrationsOverviewPanel({ onSelect }: { onSelect: (id: string) => void }) {
  const isAdmin = useIsAdmin();
  const [docs, setDocs] = useState<IntegrationDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAllIntegrations().then((d) => { setDocs(d); setLoading(false); });
  }, []);

  const reload = async () => setDocs(await getAllIntegrations());

  const toggle = async (id: string, enabled: boolean) => {
    await setIntegrationEnabled(id as IntegrationDoc["id"], enabled);
    await reload();
  };

  const PANEL_ID_BY_INTEGRATION: Record<string, string> = {
    ai: "ai", gws: "gws", slack: "slack", email: "email",
  };

  if (loading) return <Spinner />;

  const grouped = {
    identity: docs.filter((d) => d.type === "identity"),
    notification: docs.filter((d) => d.type === "notification"),
    ai: docs.filter((d) => d.type === "ai"),
  };

  const sectionTitle = (s: string) => s === "identity" ? "Identity providers (sign-in & user sync)" : s === "notification" ? "Notification providers" : "AI providers";

  const card = (d: IntegrationDoc) => {
    const detailPanel = PANEL_ID_BY_INTEGRATION[d.id];
    const stub = d.id === "m365" || d.id === "teams";
    return (
      <div key={d.id} className="card" style={{ padding: 14, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <strong style={{ fontSize: 14 }}>{d.displayName}</strong>
              {d.required && <span className="badge" style={{ background: "var(--gray)", color: "var(--text2)", fontSize: 10 }}>required</span>}
              {stub && <span className="badge" style={{ background: "var(--orange-l)", color: "var(--orange-t)", fontSize: 10 }}>coming soon</span>}
            </div>
            <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 2 }}>{d.description}</div>
            {d.requiredSecrets.length > 0 && (
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 4 }}>
                Required secret{d.requiredSecrets.length > 1 ? "s" : ""}: {d.requiredSecrets.map((s) => <code key={s} style={{ marginRight: 6 }}>{s}</code>)}
              </div>
            )}
          </div>
          {/* "Configure" is navigation, not a write — non-admins keep it so
              they can still read each integration's settings. */}
          {detailPanel && (
            <button className="btn btn-secondary btn-sm" onClick={() => onSelect(detailPanel)}>Configure</button>
          )}
          {isAdmin
            ? <Toggle value={d.enabled} onChange={(v) => toggle(d.id, v)} />
            : <span className="badge" style={{ background: d.enabled ? "var(--green-l)" : "var(--gray)", color: d.enabled ? "var(--green-t)" : "var(--text2)", fontSize: 10 }}>
                {d.enabled ? "enabled" : "disabled"}
              </span>}
        </div>
      </div>
    );
  };

  const runMigration = async () => {
    if (!confirm("Migrate any legacy config/{ai,gws,slack,email} docs into integrations/{id}? Safe to re-run.")) return;
    try {
      const fn = httpsCallable<unknown, { migrated: string[] }>(getFunctions(), "migrateLegacyConfigDocs");
      const res = await fn({});
      toast(res.data.migrated.length ? `Migrated: ${res.data.migrated.join(", ")}` : "Nothing to migrate — already clean.");
      await reload();
    } catch (e) {
      toast(`Migration failed: ${(e as Error).message}`, "error");
    }
  };

  return (
    <>
      {(Object.keys(grouped) as (keyof typeof grouped)[]).map((section) => (
        <div key={section} style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 13, color: "var(--text2)", marginBottom: 10, textTransform: "uppercase", letterSpacing: .5 }}>
            {sectionTitle(section)}
          </h3>
          {grouped[section].map(card)}
        </div>
      ))}
      {isAdmin && (
        <div className="card" style={{ padding: 14, background: "var(--gray)" }}>
          <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8 }}>
            <strong>Maintenance:</strong> If you upgraded from a pre-integrations build, click below to copy any legacy <code>config/{`{ai,gws,slack,email}`}</code> docs into the new <code>integrations/</code> collection. Idempotent.
          </div>
          <button className="btn btn-secondary btn-sm" onClick={runMigration}>Migrate legacy config docs</button>
        </div>
      )}
    </>
  );
}

// ─── Secrets (API Keys) Panel ──────────────────────────────────────

function SecretsPanel() {
  const isAdmin = useIsAdmin();
  const { helpDeskUser, firebaseUser } = useAuth();
  const [secrets, setSecrets] = useState<SecretMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<string | null>(null);
  const [newValue, setNewValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const fn = httpsCallable<unknown, { secrets: SecretMetadata[] }>(getFunctions(), "listSecretsMetadata");
      const res = await fn({});
      setSecrets(res.data.secrets);
    } catch (e) {
      toast(`Failed to load secrets: ${(e as Error).message ?? "function not deployed"}`, "error");
    }
    setLoading(false);
  };

  // listSecretsMetadata is admin-only and would just throw for an agent —
  // don't call it and show an explanation instead of an error toast.
  useEffect(() => {
    if (isAdmin) reload();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const save = async (name: string) => {
    if (!newValue) return;
    setSaving(true);
    try {
      const fn = httpsCallable(getFunctions(), "setSecret");
      await fn({ name, value: newValue });
      toast(`${name} saved`);
      setEditing(null);
      setNewValue("");
      await reload();
    } catch (e) {
      toast(`Failed to save: ${(e as Error).message}`, "error");
    }
    setSaving(false);
  };

  const saveAndTest = async (name: string) => {
    if (!newValue) return;
    const guide = SECRET_GUIDES[name];
    const adminEmail = firebaseUser?.email || helpDeskUser?.email;
    if (!guide?.emailProvider || !adminEmail) {
      toast("Test only available for email provider keys", "error");
      return;
    }
    setTesting(true);
    try {
      // Save the secret
      const setFn = httpsCallable(getFunctions(), "setSecret");
      await setFn({ name, value: newValue });

      // Auto-switch the email integration's provider so future sends use it
      try {
        await saveIntegrationConfig<Partial<EmailIntegrationConfig>>("email", { provider: guide.emailProvider });
      } catch { /* non-critical */ }

      // Fire a test email via this specific provider, regardless of saved config
      const testFn = httpsCallable(getFunctions(), "sendTestEmail");
      await testFn({ to: adminEmail, provider: guide.emailProvider });

      toast(`${name} saved · email provider set to ${guide.emailProvider} · test sent to ${adminEmail}`);
      setEditing(null);
      setNewValue("");
      await reload();
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`, "error");
    }
    setTesting(false);
  };

  const remove = async (name: string) => {
    if (!confirm(`Delete secret ${name}? Any function depending on it will fail until you set a new value.`)) return;
    try {
      const fn = httpsCallable(getFunctions(), "deleteSecret");
      await fn({ name });
      toast(`${name} deleted`);
      await reload();
    } catch (e) {
      toast(`Failed to delete: ${(e as Error).message}`, "error");
    }
  };

  if (loading) return <Spinner />;

  return (
    <SCard title="API Keys & Secrets" subtitle="Stored encrypted in Google Secret Manager — never in Firestore"
      icon="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"
      iconBg="var(--orange-l)" iconColor="var(--orange-t)"
    >
      {!isAdmin && (
        <div style={{ fontSize: 13, color: "var(--text2)" }}>
          Secret names and status are visible to administrators only. Values are never
          readable from this UI by anyone.
        </div>
      )}
      {isAdmin && secrets.length === 0 && (
        <div style={{ fontSize: 13, color: "var(--text2)" }}>
          No secrets registered yet. Enable an integration that requires secrets to see them here.
        </div>
      )}
      {secrets.map((s) => (
        <div key={s.name} style={{ padding: "12px 0", borderBottom: "0.5px solid var(--border)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <code style={{ fontSize: 12, fontWeight: 600 }}>{s.name}</code>
                {s.exists ? (
                  <span className="badge" style={{ background: "var(--green-l)", color: "var(--green-t)", fontSize: 10 }}>set</span>
                ) : (
                  <span className="badge" style={{ background: "var(--orange-l)", color: "var(--orange-t)", fontSize: 10 }}>not set</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 3 }}>{s.description}</div>
              {s.exists && s.updateTime && (
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>
                  Last updated {new Date(s.updateTime).toLocaleString()}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(s.name); setNewValue(""); }}>
                {s.exists ? "Update" : "Set"}
              </button>
              {s.exists && (
                <button className="btn btn-danger btn-sm" onClick={() => remove(s.name)}>Delete</button>
              )}
            </div>
          </div>
          {editing === s.name && (
            <div style={{ marginTop: 10, padding: 12, background: "var(--gray)", borderRadius: 6 }}>
              {SECRET_GUIDES[s.name] && (
                <div style={{ marginBottom: 12, padding: 10, background: "var(--surface)", borderRadius: 6, border: "0.5px solid var(--border)" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{SECRET_GUIDES[s.name].title}</div>
                  {SECRET_GUIDES[s.name].intro && (
                    <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8 }}>{SECRET_GUIDES[s.name].intro}</div>
                  )}
                  <ol style={{ fontSize: 12, color: "var(--text)", paddingLeft: 18, margin: "6px 0", lineHeight: 1.55 }}>
                    {SECRET_GUIDES[s.name].steps.map((step, idx) => (
                      <li key={idx} style={{ marginBottom: 3 }}>{step}</li>
                    ))}
                  </ol>
                  <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                    {SECRET_GUIDES[s.name].signupUrl && (
                      <a href={SECRET_GUIDES[s.name].signupUrl} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: "var(--blue)" }}>Sign up / open dashboard →</a>
                    )}
                    {SECRET_GUIDES[s.name].docsUrl && (
                      <a href={SECRET_GUIDES[s.name].docsUrl} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: "var(--blue)" }}>API docs →</a>
                    )}
                  </div>
                  {SECRET_GUIDES[s.name].notes && SECRET_GUIDES[s.name].notes!.map((n, i) => (
                    <div key={i} style={{ fontSize: 11, color: "var(--text2)", marginTop: 6, fontStyle: "italic" }}>{n}</div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <input className="fi" type="password" autoFocus placeholder="Paste secret value" style={{ flex: 1, minWidth: 200, fontFamily: "var(--font-mono)" }}
                  value={newValue} onChange={(e) => setNewValue(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={() => save(s.name)} disabled={saving || testing || !newValue}>
                  {saving ? <Spinner size={12} color="white" /> : "Save"}
                </button>
                {SECRET_GUIDES[s.name]?.emailProvider && (
                  <button className="btn btn-green btn-sm" onClick={() => saveAndTest(s.name)} disabled={saving || testing || !newValue}>
                    {testing ? <Spinner size={12} color="var(--green-t)" /> : "Save & Test"}
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(null); setNewValue(""); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 14, fontStyle: "italic" }}>
        Values are write-only — once saved, this UI never reads them back. To rotate, click Update and paste the new value.
      </div>
    </SCard>
  );
}

// ─── Main Settings component ──────────────────────────────────────

export default function Settings() {
  useDocumentTitle("Settings");
  const [active, setActive] = useState("branding");
  const { helpDeskUser } = useAuth();
  const isAdmin = helpDeskUser?.role === "admin";

  // Every panel here is admin-only except "My Notifications", which edits
  // the signed-in user's own alert preferences.
  const panels: Record<string, React.ReactNode> = {
    branding:     <AdminGate><BrandingPanel /></AdminGate>,
    users:        <AdminGate><UsersPanel /></AdminGate>,
    csv:          <AdminGate><CsvPanel /></AdminGate>,
    gws:          <AdminGate><GWSPanel /></AdminGate>,
    slack:        <AdminGate><SlackPanel /></AdminGate>,
    ai:           <AdminGate><AiPanel /></AdminGate>,
    sla:          <AdminGate><SlaPanel /></AdminGate>,
    email:        <AdminGate><EmailPanel /></AdminGate>,
    integrations: <AdminGate><IntegrationsOverviewPanel onSelect={setActive} /></AdminGate>,
    secrets:      <AdminGate><SecretsPanel /></AdminGate>,
  };

  return (
    <AdminContext.Provider value={isAdmin}>
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Settings nav */}
      <nav style={{ width: 200, background: "var(--surface)", borderRight: "0.5px solid var(--border)", padding: "8px 0", flexShrink: 0, overflowY: "auto" }}>
        {[
          { section: "General",      items: PANELS.filter((p) => ["branding","ai","sla"].includes(p.id)) },
          { section: "Users",        items: PANELS.filter((p) => ["users","csv"].includes(p.id)) },
          { section: "Integrations", items: PANELS.filter((p) => ["integrations","gws","slack","email"].includes(p.id)) },
          { section: "Security",     items: PANELS.filter((p) => ["secrets"].includes(p.id)) },
        ].map(({ section, items }) => (
          <div key={section}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", letterSpacing: .6, textTransform: "uppercase", padding: "0 14px 4px", marginTop: 8 }}>{section}</div>
            {items.map((item) => (
              <div key={item.id} onClick={() => setActive(item.id)}
                style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 14px", cursor: "pointer", fontSize: 13,
                  color: active === item.id ? "var(--blue-t)" : "var(--text)",
                  background: active === item.id ? "var(--blue-l)" : "transparent",
                  fontWeight: active === item.id ? 500 : 400 }}
                onMouseEnter={(e) => { if (active !== item.id) e.currentTarget.style.background = "var(--gray)"; }}
                onMouseLeave={(e) => { if (active !== item.id) e.currentTarget.style.background = "transparent"; }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill={active === item.id ? "var(--blue)" : "var(--text2)"}>
                  <path d={item.icon} />
                </svg>
                {item.label}
              </div>
            ))}
          </div>
        ))}
      </nav>

      {/* Panel content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <h1 style={{ marginBottom: 3 }}>
          {PANELS.find((p) => p.id === active)?.label}
        </h1>
        <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>
          {active === "branding"     && "Customize your helpdesk appearance"}
          {active === "users"        && "Create, manage, and disable user accounts"}
          {active === "csv"          && "Bulk import or update users from a spreadsheet"}
          {active === "integrations" && "Enable, disable, and configure each integration"}
          {active === "gws"          && "Auto-import and sync users from Google Workspace"}
          {active === "slack"        && "Post ticket notifications to your Slack workspace"}
          {active === "ai"           && "Choose your AI provider and configure triage behavior"}
          {active === "sla"          && "Define response and resolution targets by priority"}
          {active === "email"        && "Configure transactional email notifications"}
          {active === "secrets"      && "Store API keys and tokens — values are write-only"}
        </p>
        {panels[active]}
      </div>
    </div>
    </AdminContext.Provider>
  );
}
